/**
 * App-tile data for /looker: pure builders, no I/O and no i18n (plan
 * 2026-10-12-looker-d1-phase3, Task 4). Each builder turns what an existing
 * service already returns into the box's figures plus a labelled `table`, the
 * one shape the chat and the CSV read (Task 6). Column labels arrive through
 * `label`, so the language stays with the caller.
 *
 * NULL means "not measurable", never 0: a ratio over a 0 or NULL denominator
 * is null, and the table carries it as a null cell.
 */
import { isHiddenPage } from '@/services/hiddenPages';
import { isAbandoned } from '@/services/mediaApi/reapDecision';
import { ACTIVE_JOB_STATUSES } from '@/services/syncJobShared';
import { MEASUREMENT_PLATFORMS, buildSyncPills } from '@/services/dashboard/dashboardEvents';
import type { DashboardEvent, PlatformSyncStatus } from '@/services/dashboard/dashboardEvents';
import type { OutcomePeriod, OutcomeSpend } from '@/services/outcomeImport/outcomeImportService';
import type { DataQualityAction } from '@/services/mediaIntelligenceTypes';
import { withoutFloatNoise } from './lookerNumber';
import type { BudgetPacingView } from './budgetPacingData';

/** Box 7 reads /insights' own window, not the page's period (ruling 6). */
const ATTENTION_WINDOW = 30 as const;
const ATTENTION_MAX = 3;

/**
 * Every key `label` is asked for: column headers, plus the few cell values
 * that are words rather than data (states, severities, the 30-day window).
 */
export type AppLabelKey =
  | 'channel' | 'window'
  | 'days' | 'leads' | 'bookings' | 'sales' | 'revenue' | 'spend' | 'costPerLead' | 'mediaRoas'
  | 'item' | 'where' | 'last30Days'
  | 'finding' | 'action' | 'severity'
  | 'blocking' | 'degraded'
  // Box 8: the window each row's figures cover (chat test 2026-10-22).
  | 'allTime' | 'asOfToday' | 'selectedPeriod'
  // Box 6 (budgetPacing): its item/value rows and the words its status cells hold.
  | 'value' | 'status' | 'pacingPct' | 'plan' | 'projected' | 'dataThrough' | 'currency'
  | 'month' | 'pacingDays' | 'note' | 'channelSum'
  | 'pacingUnder' | 'pacingOnTrack' | 'pacingOver' | 'pacingTooEarly' | 'pacingNoPlan' | 'pacingNoPlanInCurrency' | 'pacingPickCurrency'
  | 'pacingUnknownCurrency' | 'pacingDerived' | 'pacingNoDataYet'
  // Box 10 (outcomes): the words its split table holds (rules-04 B1), and its days footnote.
  | 'outcomesMixedCurrency' | 'outcomesUnknownCurrency' | 'unknownCurrency' | 'outcomesDays';
/** `vars` fills a sentence's placeholders (the outcomes footnote's days); a column label takes none. */
export type LabelFn = (key: AppLabelKey, vars?: Record<string, unknown>) => string;

/** Columns and rows keyed by the translated column label. */
export interface AppTable {
  columns: string[];
  rows: Array<Record<string, string | null>>;
  /** The columns whose cells are figures: the file writes a NULL there as the box's "—" (rules-04 D review I2). The rest are labels. */
  values?: string[];
}

/** One ad channel's sync, for the /looker "data as of" pill (the retired freshness box). */
export interface FreshChannel {
  platform: string;
  lastSyncedAt: string | null;
  dataThrough: string | null;
  state: 'ok' | 'stale' | 'error' | 'never';
  error: string | null;
  /** Whole days between today (Bangkok) and the date the state was judged on; null = never synced. From Home's pill. */
  ageDays: number | null;
  /** Which date `ageDays` counts from: the newest data date, or (no data at all) the last sync. */
  ageSource: 'data' | 'sync' | null;
}

/** Chunk counts and window of one platform or one ad account in the open history download. */
export interface BackfillCounts {
  queued: number;
  running: number;
  stuck: number;
  done: number;
  failed: number;
  from: string | null;
  to: string | null;
}

/** One ad account's share of a platform's open history download. */
export interface BackfillAccount extends BackfillCounts {
  /** null = the "unassigned" row: jobs that carry no account. */
  accountId: string | null;
  /** The account name, or the id's first 8 characters when it has none: never blank. null only for the unassigned row. */
  name: string | null;
  /**
   * The newest gap-free run of done days: it ends at `to` when `to` is in, else at
   * the latest done chunk (owner ruling 2026-10-13). Both null = nothing done yet.
   */
  availableFrom: string | null;
  availableTo: string | null;
}

/** One platform's share of the open history download (the /looker filter-bar pill). */
export interface BackfillPlatform extends BackfillCounts {
  platform: string;
  /** Jobs with no account count above and are listed as one unassigned row (accountId null), last. */
  accounts: BackfillAccount[];
}

/**
 * Time left for the whole open window. The worker runs one global FIFO across
 * every workspace, and other workspaces' jobs are invisible under RLS, so on its
 * own this is a LOWER bound (lowerBound true, queueAhead null). When
 * `sync_queue_ahead` could be read, the other jobs are priced in and it is an
 * estimate (lowerBound false). minutes null = cannot estimate (or nothing left).
 */
export interface BackfillEta {
  minutes: number | null;
  lowerBound: boolean;
  /** Other workspaces' jobs counted ahead of ours (queued ahead + running); null = the queue was not counted. */
  queueAhead: number | null;
}

/** What `sync_queue_ahead` returns: counts only, never whose jobs. */
export interface QueueAhead {
  ahead: number;
  running: number;
}

/** What the backfill loader hands the pill, the CSV and the chat. */
export interface BackfillSummary {
  platforms: BackfillPlatform[];
  eta: BackfillEta;
}

export interface AttentionItem {
  text: string;
  link: string;
  severity: 1 | 2 | 3 | 4;
}

/** One sync_jobs row as the backfill loader selects it. */
export interface BackfillJobRow {
  platform: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  started_at: string | null;
  heartbeat_at: string | null;
  created_at: string;
  account_id: string | null;
  completed_at: string | null;
}

/**
 * Box 10's money (rules-04 B1, FR-X-02). `one`: every non-zero spend of the outcome days is in one known currency;
 * with none, the only known currency of the zero (or NULL) rows, else null (money then shows with no currency marker).
 * `split`: non-zero spend in more than one currency, or on an account of unknown currency: spend shows per currency,
 * and Cost per Lead and Media ROAS are not computed. Non-zero, not > 0: a negative adjustment in another currency
 * (media_ad_set_metrics.cost has no CHECK) is never added into the one currency's total.
 */
export type OutcomeMoney =
  | { kind: 'one'; currency: string | null }
  | { kind: 'split'; reason: 'mixed' | 'unknown'; spends: Array<{ currency: string | null; spend: number }> };

export type AppTileData =
  | {
      widget: 'outcomes';
      outcomes: OutcomePeriod;
      costPerLead: number | null;
      mediaRoas: number | null;
      money: OutcomeMoney;
      table: AppTable;
      hidden: boolean;
      /** The box's footnote, in its words: which days the figures count. Absent when no day of the period has outcomes. */
      daysNote?: string;
    }
  | {
      widget: 'attention';
      events: AttentionItem[];
      windowDays: 30;
      /** The efficiency read hit its row cap: the ranking covers part of the set (as /insights says). */
      truncated: boolean;
      table: AppTable;
      hidden: false;
    }
  | {
      widget: 'dataQuality';
      actions: DataQualityAction[];
      table: AppTable;
      hidden: false;
      /** The window cell, by column label, of the coverage lines the chat appends to this table: the selected period. */
      coverageWindow?: Record<string, string>;
    }
  /** Box 6: this month against the approved plan; ignores the page filters except the currency. */
  | { widget: 'budgetPacing'; view: BudgetPacingView; table: AppTable; hidden: false };

export type Cell = string | null;

/** Builds the labelled table from column keys and rows keyed the same way; `values` names its figure columns. */
export const tableOf = (
  label: LabelFn, keys: AppLabelKey[], rows: Array<Partial<Record<AppLabelKey, Cell>>>, values: readonly AppLabelKey[] = [],
): AppTable => ({
  columns: keys.map((k) => label(k)),
  rows: rows.map((r) => Object.fromEntries(keys.map((k) => [label(k), r[k] ?? null]))),
  ...(values.length > 0 ? { values: values.map((k) => label(k)) } : {}),
});

/** The chat and the CSV both read this text, so a ratio never reaches either as 0.30000000000000004. */
export const numCell = (n: number | null): Cell => (n === null ? null : String(withoutFloatNoise(n)));

/** Groups by platform, keeping the order platforms first appear in. */
const groupBy = <T>(items: readonly T[], keyOf: (x: T) => string): Array<[string, T[]]> =>
  [...items.reduce((m, x) => m.set(keyOf(x), [...(m.get(keyOf(x)) ?? []), x]), new Map<string, T[]>())];

/** The latest of some ISO timestamps, compared as instants (time zones vary). */
const latestInstant = (values: Array<string | null>): string | null =>
  values.reduce<string | null>((best, v) => {
    if (v === null || Number.isNaN(Date.parse(v))) return best;
    return best === null || Date.parse(v) > Date.parse(best) ? v : best;
  }, null);

/** The latest of some YYYY-MM-DD dates (plain dates sort as strings). */
const latestDate = (values: Array<string | null>): string | null =>
  values.filter((v): v is string => v !== null).sort().pop() ?? null;

/** Home's pill state in /looker's vocabulary. */
const PILL_STATE = { err: 'error', never: 'never', warn: 'stale', ok: 'ok' } as const;

/**
 * Per ad channel, judged by Home's own rule: `buildSyncPills` ages each platform
 * by its newest DATA date (the last sync only when it holds no data), in days
 * against `todayISO`, and warns past STALE_AFTER_DAYS. It is derived from the
 * pills, not re-implemented, so the two surfaces cannot drift apart.
 * [] = no ad channel connected (analytics is not one).
 */
export const freshnessOf = (statuses: readonly PlatformSyncStatus[], todayISO: string): FreshChannel[] => {
  const ad = statuses.filter((s) => !MEASUREMENT_PLATFORMS.has(s.platform));
  const pills = new Map(buildSyncPills([...ad], todayISO).map((p) => [p.platform, p]));
  return groupBy(ad, (s) => s.platform).map(([platform, list]): FreshChannel => {
    const pill = pills.get(platform);
    return {
      platform,
      lastSyncedAt: latestInstant(list.map((s) => s.lastSyncedAt)),
      dataThrough: latestDate(list.map((s) => s.dataThrough)),
      state: pill ? PILL_STATE[pill.state] : 'never',
      error: pill?.error ?? null,
      ageDays: pill?.ageDays ?? null,
      ageSource: pill?.ageSource ?? null,
    };
  });
};

/**
 * Not fresh: newest data more than 3 days behind today (as Home's strip), never synced, or a sync error.
 * Only an `ok` channel may be presented as fresh.
 */
export const isNotFresh = (c: FreshChannel): boolean => c.state !== 'ok';

const isOpen = (j: BackfillJobRow): boolean => (ACTIVE_JOB_STATUSES as readonly string[]).includes(j.status);

/**
 * The current backfill run: dated jobs created at or after the oldest OPEN
 * dated job. Nothing open → nothing (the pill renders nothing).
 */
export const openWindowJobs = (jobs: readonly BackfillJobRow[]): BackfillJobRow[] => {
  const dated = jobs.filter((j) => j.start_date !== null);
  const anchor = dated.filter(isOpen).map((j) => Date.parse(j.created_at)).reduce<number | null>(
    (min, v) => (min === null || v < min ? v : min), null,
  );
  return anchor === null ? [] : dated.filter((j) => Date.parse(j.created_at) >= anchor);
};

const DONE_STATUSES = new Set(['success', 'no_data', 'partial']);
const isDone = (j: BackfillJobRow): boolean => DONE_STATUSES.has(j.status);
const isStuck = (j: BackfillJobRow, now: Date): boolean =>
  j.status === 'running' && isAbandoned({ startedAt: j.started_at, heartbeatAt: j.heartbeat_at, now });

const countsOf = (list: readonly BackfillJobRow[], now: Date): BackfillCounts => {
  const running = list.filter((j) => j.status === 'running');
  const stuck = running.filter((j) => isStuck(j, now)).length;
  const starts = list.map((j) => j.start_date).filter((d): d is string => d !== null).sort();
  return {
    queued: list.filter((j) => j.status === 'queued').length,
    running: running.length - stuck,
    stuck,
    done: list.filter(isDone).length,
    failed: list.filter((j) => j.status === 'failed').length,
    from: starts[0] ?? null,
    to: latestDate(list.map((j) => j.end_date)),
  };
};

const DAY_MS = 86_400_000;
const dayBefore = (date: string): string => new Date(Date.parse(`${date}T00:00:00Z`) - DAY_MS).toISOString().slice(0, 10);

type DatedJob = BackfillJobRow & { start_date: string; end_date: string };
const doneDated = (jobs: readonly BackfillJobRow[]): DatedJob[] =>
  jobs.filter((j): j is DatedJob => isDone(j) && j.start_date !== null && j.end_date !== null);

const walkBack = (done: readonly DatedJob[], cursor: string, earliest: string | null): string | null => {
  const covering = done.filter((j) => j.start_date <= cursor && cursor <= j.end_date).map((j) => j.start_date).sort();
  return covering.length === 0 ? earliest : walkBack(done, dayBefore(covering[0]), covering[0]);
};

/**
 * The earliest day reachable from `windowEnd` walking back through done chunks
 * without a gap. null = `windowEnd` itself is not in a done chunk.
 */
export const availableFrom = (jobs: readonly BackfillJobRow[], windowEnd: string | null): string | null =>
  windowEnd === null ? null : walkBack(doneDated(jobs), windowEnd, null);

/**
 * The newest gap-free run of done days. It ends at `windowEnd` when that day is
 * done; otherwise (newest chunk failed or still waiting) at the latest done
 * chunk's last day. null = no window end or nothing done.
 */
export const availableRange = (
  jobs: readonly BackfillJobRow[],
  windowEnd: string | null,
): { from: string; to: string } | null => {
  if (windowEnd === null) return null;
  const done = doneDated(jobs);
  const anchor = availableFrom(done, windowEnd) !== null
    ? windowEnd
    : latestDate(done.map((j) => j.end_date));
  const from = anchor === null ? null : walkBack(done, anchor, null);
  return anchor === null || from === null ? null : { from, to: anchor };
};

const rangeFields = (
  jobs: readonly BackfillJobRow[],
  windowEnd: string | null,
): Pick<BackfillAccount, 'availableFrom' | 'availableTo'> => {
  const range = availableRange(jobs, windowEnd);
  return { availableFrom: range?.from ?? null, availableTo: range?.to ?? null };
};

const nameOf = (accountId: string, names: Readonly<Record<string, string>>): string => {
  const name = names[accountId]?.trim();
  return name ? name : accountId.slice(0, 8);
};

const accountsOf = (list: readonly BackfillJobRow[], now: Date, names: Readonly<Record<string, string>>): BackfillAccount[] => {
  const assigned = groupBy(list.filter((j) => j.account_id !== null), (j) => j.account_id ?? '').map(([accountId, jobs]): BackfillAccount => {
    const counts = countsOf(jobs, now);
    return { accountId, name: nameOf(accountId, names), ...counts, ...rangeFields(jobs, counts.to) };
  });
  const orphans = list.filter((j) => j.account_id === null);
  if (orphans.length === 0) return assigned;
  const counts = countsOf(orphans, now);
  return [...assigned, { accountId: null, name: null, ...counts, ...rangeFields(orphans, counts.to) }];
};

/**
 * Progress of the open history download, per platform and per account; [] =
 * none open. `names` maps account id → name; a missing one shows the id prefix.
 */
export const backfillOf = (
  jobs: readonly BackfillJobRow[],
  now: Date,
  names: Readonly<Record<string, string>> = {},
): BackfillPlatform[] =>
  groupBy(openWindowJobs(jobs), (j) => j.platform).map(([platform, list]) => ({
    platform, ...countsOf(list, now), accounts: accountsOf(list, now, names),
  }));

/** Below this many timed chunks a platform's chunk duration is not estimated. */
const ETA_MIN_SAMPLES = 3;

const median = (values: readonly number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** Seconds a done chunk took, or null when it was not timed. */
const secondsOf = (j: BackfillJobRow): number | null => {
  if (!isDone(j) || j.started_at === null || j.completed_at === null) return null;
  const s = (Date.parse(j.completed_at) - Date.parse(j.started_at)) / 1000;
  return Number.isNaN(s) || s < 0 ? null : s;
};

/**
 * Time left for the open window: per platform, each queued chunk counts the
 * median done-chunk duration, and each non-stuck running chunk counts what is
 * left of it, max(0, median − elapsed since it started) (the whole median when
 * it has no start time). A platform with work left and fewer than 3 timed
 * chunks → null; nothing left anywhere (only stuck or failed) → null, never 0.
 *
 * `queue` (from `sync_queue_ahead`) prices the other workspaces' jobs the worker
 * runs first: (ahead + running) × typical, where typical is the median of ALL
 * this workspace's timed chunks across platforms (same 3-sample minimum). The
 * other jobs' platforms are unknown, so one typical duration stands in for all
 * of them: an estimate, not a bound. Without `queue` the result stays a lower
 * bound.
 */
export const backfillEta = (jobs: readonly BackfillJobRow[], now: Date, queue?: QueueAhead | null): BackfillEta => {
  const window = openWindowJobs(jobs);
  const perPlatform = groupBy(window, (j) => j.platform).map(([, list]) => {
    const queued = list.filter((j) => j.status === 'queued');
    const running = list.filter((j) => j.status === 'running' && !isStuck(j, now));
    const timed = list.map(secondsOf).filter((s): s is number => s !== null);
    if (timed.length < ETA_MIN_SAMPLES) return { remaining: queued.length + running.length, seconds: null };
    const typical = median(timed);
    const left = (j: BackfillJobRow): number => {
      const startedMs = j.started_at === null ? Number.NaN : Date.parse(j.started_at);
      return Number.isNaN(startedMs) ? typical : Math.max(0, typical - (now.getTime() - startedMs) / 1000);
    };
    return { remaining: queued.length + running.length, seconds: queued.length * typical + running.reduce((n, j) => n + left(j), 0) };
  });
  const open = perPlatform.filter((p) => p.remaining > 0);
  const others = queue ? queue.ahead + queue.running : null;
  const meta = { lowerBound: !queue, queueAhead: others };
  if (open.length === 0 || open.some((p) => p.seconds === null)) return { minutes: null, ...meta };
  const own = open.reduce((n, p) => n + (p.seconds ?? 0), 0);
  if (!others) return { minutes: Math.ceil(own / 60), ...meta };
  const allTimed = window.map(secondsOf).filter((s): s is number => s !== null);
  if (allTimed.length < ETA_MIN_SAMPLES) return { minutes: null, ...meta };
  return { minutes: Math.ceil((own + others * median(allTimed)) / 60), ...meta };
};

/** Hours shown with one decimal below this; whole hours from here on. */
const ETA_DECIMAL_HOURS = 10;

/**
 * How an ETA is spoken: minutes below an hour, then hours (one decimal below
 * 10 h). Rounded DOWN, so a fallback lower bound stays one. No i18n here.
 */
export const etaDisplay = (minutes: number | null): { unit: 'unknown' } | { unit: 'minutes' | 'hours'; n: number } => {
  if (minutes === null) return { unit: 'unknown' };
  if (minutes < 60) return { unit: 'minutes', n: minutes };
  // Tenths of an hour are 6-minute steps: integer maths, no 1.1 × 10 float noise.
  return { unit: 'hours', n: minutes < ETA_DECIMAL_HOURS * 60 ? Math.floor(minutes / 6) / 10 : Math.floor(minutes / 60) };
};

/** Every chunk of one platform's window, whatever its state. */
export const backfillTotal = (p: BackfillPlatform): number => p.done + p.queued + p.running + p.stuck + p.failed;

/** A ratio is null over a null or 0 denominator, and null over a null numerator. */
const ratioOf = (num: number | null, den: number | null): number | null =>
  num === null || den === null || den === 0 ? null : num / den;

/** Box 10's money from the server's buckets, in their order (see OutcomeMoney). */
export const outcomeMoneyOf = (rows: readonly OutcomeSpend[]): OutcomeMoney => {
  // Not > 0: a negative bucket in another currency splits the box too, never added into the one total.
  // A currency whose rows are all 0 (or NULL) still never makes the box "mixed".
  const paid = rows.flatMap((r) => (r.spend !== null && r.spend !== 0 ? [{ currency: r.currency, spend: r.spend }] : []));
  if (paid.some((r) => r.currency === null)) return { kind: 'split', reason: 'unknown', spends: paid };
  if (new Set(paid.map((r) => r.currency)).size > 1) return { kind: 'split', reason: 'mixed', spends: paid };
  if (paid.length > 0) return { kind: 'one', currency: paid[0].currency };
  const known = new Set(rows.flatMap((r) => (r.currency === null ? [] : [r.currency])));
  return { kind: 'one', currency: known.size === 1 ? [...known][0] : null };
};

/** Box 10's figure columns, in this order in its table and in `values`: the chat pairs each with its label there. */
export const OUTCOME_FIGURES = ['days', 'leads', 'bookings', 'sales', 'revenue', 'spend', 'costPerLead', 'mediaRoas'] as const satisfies readonly AppLabelKey[];

/**
 * Box 10: imported outcomes for the period against the spend of the same days
 * (migration 113), never adding currencies together (migration 125). `days`
 * travels in the table so the chat and the CSV carry the coverage the ratios
 * were computed on; `currency` names the one the money is in, and a split box
 * gives each currency's spend its own row, with no total across them.
 */
export const outcomesOf = (o: OutcomePeriod, label: LabelFn): AppTileData => {
  const money = outcomeMoneyOf(o.spendByCurrency);
  const split = money.kind === 'split';
  // spend 0 or null → CPL null (never ฿0); leads 0 or null → CPL null (never ∞).
  // spend 0 with leads means no media spend reached those days: not a ฿0 lead.
  // Spend in several currencies, or in an unknown one, has no single total to divide.
  const costPerLead = split || o.spend === 0 ? null : ratioOf(o.spend, o.leads);
  const mediaRoas = split ? null : ratioOf(o.revenue, o.spend);
  const figures: AppLabelKey[] = [...OUTCOME_FIGURES];
  const keys: AppLabelKey[] = [...figures, 'currency'];
  const tile = (table: AppTable): AppTileData => ({
    widget: 'outcomes', outcomes: o, costPerLead, mediaRoas, money, table, hidden: !o.anyImported,
    ...(o.days > 0 ? { daysNote: label('outcomesDays', { days: o.days }) } : {}),
  });
  // Imported, but no outcome day in this period: an empty table, so the chat says
  // "empty" and the CSV writes its empty note, as the box says "nothing in this range".
  if (o.days === 0) return tile(tableOf(label, keys, [], figures));
  const counted = {
    days: String(o.days), leads: numCell(o.leads), bookings: numCell(o.bookings), sales: numCell(o.sales), revenue: numCell(o.revenue),
  };
  if (money.kind === 'one') {
    return tile(tableOf(label, keys, [{
      ...counted, spend: numCell(o.spend), costPerLead: numCell(costPerLead), mediaRoas: numCell(mediaRoas), currency: money.currency,
    }], figures));
  }
  // Split: each spend sits in a numeric cell beside its own currency; the reason is the first row's note.
  return tile(tableOf(label, [...keys, 'note'], [
    { ...counted, note: label(money.reason === 'mixed' ? 'outcomesMixedCurrency' : 'outcomesUnknownCurrency') },
    ...money.spends.map((e) => ({ currency: e.currency ?? label('unknownCurrency'), spend: numCell(e.spend) })),
  ], figures));
};

/** Box 7: the three loudest events, already ranked by buildDashboardEvents. */
export const attentionOf = (
  events: DashboardEvent[],
  text: (e: DashboardEvent) => string,
  label: LabelFn,
  truncated: boolean,
): AppTileData => {
  const items = events.slice(0, ATTENTION_MAX).map((e): AttentionItem => ({ text: text(e), link: e.link, severity: e.severity }));
  // A hidden page's link stays out of the chat and the files (rules-04 D review I3); the box decides its own.
  const table = tableOf(label, ['item', 'where', 'window'], items.map((i) => ({
    item: i.text, where: isHiddenPage(i.link) ? null : i.link, window: label('last30Days'),
  })));
  return { widget: 'attention', events: items, windowDays: ATTENTION_WINDOW, truncated, table, hidden: false };
};

/**
 * Box 8: every data-quality action, blocking first (stable otherwise). Each row names its window, as box 7's rows
 * do: an action's figures are all time or today's, never the page's period, which only the coverage lines follow.
 */
export const dataQualityOf = (actions: DataQualityAction[], label: LabelFn): AppTileData => {
  const sorted = [
    ...actions.filter((a) => a.severity === 'blocking'),
    ...actions.filter((a) => a.severity !== 'blocking'),
  ];
  const table = tableOf(label, ['channel', 'finding', 'action', 'severity', 'window'], sorted.map((a) => ({
    channel: a.channel ?? null, finding: a.finding, action: a.action, severity: label(a.severity), window: label(a.window),
  })));
  return { widget: 'dataQuality', actions: sorted, table, hidden: false, coverageWindow: { [label('window')]: label('selectedPeriod') } };
};
